import { Dispatch, type FC, SetStateAction, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PillDropdown } from 'components/Inputs/PillDropdown';
import { PILL_TYPES } from 'components/Inputs/PillDropdown/constants';
import KnowledgeService from 'services/KnowledgeService';
import { colorsButton } from 'app/colors';
import {
  StyledQuickFiltersLayout,
  StyledQuickFiltersHeading,
  StyledActionButton,
  StyledIcon,
} from '../styles';
import { useAsync } from 'react-use';

export type QuickFilterOption = { value: string; label: string };

const dummyFn = () => {};
const sourceToOption = (source: {
  id: string;
  description?: string | null;
}): QuickFilterOption => ({
  label: source.description || '—',
  value: source.id,
});

const SourceSelect: FC<{
  value: string | null;
  onChange: (option: QuickFilterOption | null) => void;
  catalogId?: string;
}> = ({ value, onChange, catalogId }) => {
  const { t } = useTranslation();

  const { value: options = [], loading } = useAsync(async () => {
    try {
      const res = await KnowledgeService.getOnlineSourceSearch({
        params: { catalog_id: catalogId },
      });
      return res.results.map(sourceToOption);
    } catch (error) {
      return [];
    }
  }, [catalogId]);

  return (
    <>
      <PillDropdown
        pillType={PILL_TYPES.singleselect.type}
        value={value}
        title={t('Source')}
        onChange={onChange}
        onOpen={dummyFn}
        onClose={dummyFn}
        options={options}
        pillDropdownProps={{
          isLoading: loading,
        }}
      />
    </>
  );
};

const StatusSelect: FC<{
  value: string | null;
  onChange: (option: QuickFilterOption | null) => void;
}> = ({ value, onChange }) => {
  const { t } = useTranslation();

  const options: QuickFilterOption[] = [
    { value: 'active', label: t('Active') },
    { value: 'disabled', label: t('Disabled') },
    { value: 'disabled_by_source', label: t('Disabled by Source') },
  ];

  return (
    <PillDropdown
      pillType={PILL_TYPES.singleselect.type}
      value={value}
      title={t('Status')}
      options={options}
      onChange={onChange}
      onOpen={dummyFn}
      onClose={dummyFn}
    />
  );
};

type QuickFiltersConfig = {
  source: string | null;
  status: string | null;
};

const defaultFilters = {
  source: null,
  status: null,
};

export const QuickFilters: FC<{
  filters: QuickFiltersConfig;
  setFilters: Dispatch<SetStateAction<{ filters: QuickFiltersConfig }>>;
  catalogId?: string;
}> = ({ filters = defaultFilters, setFilters, catalogId }) => {
  const { t } = useTranslation();

  const onChangeSource = useCallback(
    (opt: QuickFilterOption | null) => {
      setFilters((prev) => ({
        ...prev,
        page: 1,
        filters: { ...prev.filters, source: opt?.value || null },
      }));
    },
    [setFilters]
  );

  const onChangeStatus = useCallback(
    (opt: QuickFilterOption | null) => {
      setFilters((prev) => ({
        ...prev,
        page: 1,
        filters: { ...prev.filters, status: opt?.value || null },
      }));
    },
    [setFilters]
  );

  const handleClear = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      page: 1,
      filters: { source: null, status: null },
    }));
  }, [setFilters]);

  return (
    <StyledQuickFiltersLayout>
      <StyledQuickFiltersHeading
        color="var(--color-font-placeholder)"
        weight="bold"
      >
        {t('Quick Filters')}
      </StyledQuickFiltersHeading>
      <SourceSelect
        value={filters.source}
        onChange={onChangeSource}
        catalogId={catalogId}
      />
      <StatusSelect value={filters.status} onChange={onChangeStatus} />
      {Object.values(filters).filter(Boolean).length ? (
        <StyledActionButton
          title={t('Clear All Quick Filters')}
          color={colorsButton.blue}
          onClick={handleClear}
        >
          <StyledIcon icon="close-regular" />
        </StyledActionButton>
      ) : null}
    </StyledQuickFiltersLayout>
  );
};
